import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, History, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { StatusBadge } from "@/components/StatusBadge";
import { HistoryTimeline } from "@/components/HistoryTimeline";
import { ProductForm, ProductStatusForm } from "@/components/forms/ProductForms";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm">{value ?? "—"}</p>
    </div>
  );
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const [product, setProduct] = useState(null);
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [types, setTypes] = useState(["DSK"]);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [archiveError, setArchiveError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h, c, t] = await Promise.all([
        api.get(`/products/${id}`),
        api.get(`/products/${id}/history`),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
        isStaff ? api.get("/product-types") : Promise.resolve({ data: ["DSK"] }),
      ]);
      setProduct(p.data); setEvents(h.data);
      setClients((c.data || []).filter((x) => !x.is_archived));
      setTypes(t.data || ["DSK"]);
    } catch (err) {
      if (err?.response?.status === 404) navigate("/products");
    } finally { setLoading(false); }
  }, [id, isStaff, navigate]);

  useEffect(() => { load(); }, [load]);

  async function toggleArchive() {
    if (!product) return;
    setArchiving(true); setArchiveError(null);
    try {
      await api.post(`/products/${product.id}/archive`, { archived: !product.is_archived });
      setConfirmOpen(false);
      await load();
    } catch (err) { setArchiveError(apiError(err)); } finally { setArchiving(false); }
  }

  if (loading || !product) return <p className="text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-6">
      <Link to="/products" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Products
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold tracking-tight">{product.serial_number}</h2>
            <StatusBadge status={product.status} />
            {product.is_archived && <Badge variant="secondary">Archived</Badge>}
          </div>
          <p className="text-muted-foreground">{product.product_type} · {product.client_name}</p>
        </div>
        {isStaff && (
          <div className="flex flex-wrap gap-2">
            <ProductStatusForm productId={product.id} currentStatus={product.status} onSaved={load} />
            <ProductForm clients={clients} types={types} product={product} onSaved={load} />
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setConfirmOpen(true); setArchiveError(null); }}
                disabled={archiving}
                data-testid={product.is_archived ? "restore-product-btn" : "archive-product-btn"}
              >
                {product.is_archived ? "Restore" : "Archive"}
              </Button>
            )}
          </div>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!archiving) { setConfirmOpen(o); if (!o) setArchiveError(null); } }}>
        <AlertDialogContent data-testid="archive-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{product.is_archived ? "Restore this product?" : "Archive this product?"}</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-slate-900">{product.serial_number}</span>
              {product.client_name ? <> · {product.client_name}</> : null}
              {product.is_archived
                ? " will be moved back to the active Products list."
                : " will be hidden from the active Products list. You can restore it later from the Archived Products page."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {archiveError && <p className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{archiveError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving} data-testid="archive-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); toggleArchive(); }}
              disabled={archiving}
              data-testid="archive-confirm"
            >
              {archiving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {product.is_archived ? "Yes, restore" : "Yes, archive"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Card>
        <CardHeader><CardTitle className="text-base">Product Details</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Serial Number" value={product.serial_number} />
          <Field label="Product Type" value={product.product_type} />
          <Field label="Organization" value={product.client_name} />
          <Field label="Current Location" value={product.current_location} />
          <Field label="Aisle" value={product.warehouse_location} />
          <Field label="Received at Warehouse" value={product.received_date} />
          <Field label="T-00 Sticks (fixed to this DSK)" value={(product.stick1_serial || product.stick2_serial) ? [product.stick1_serial, product.stick2_serial].filter(Boolean).join(" · ") : null} />
          <Field label="Wooden Crate Box" value={product.crate ? <Link to={`/crates/${product.crate_id}`} className="text-primary hover:underline">{product.crate.crate_code}</Link> : null} />
          <Field label="Active Shipment" value={product.shipment ? <Link to={`/shipments/${product.shipment_id}`} className="text-primary hover:underline">{product.shipment.shipment_code}</Link> : null} />
          <Field label="Created" value={new Date(product.created_at).toLocaleDateString()} />
          {product.notes && (<div className="sm:col-span-2 lg:col-span-3"><Field label="Notes" value={product.notes} /></div>)}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4" /> History Timeline</CardTitle></CardHeader>
        <CardContent><HistoryTimeline events={events} /></CardContent>
      </Card>
    </div>
  );
}
